import { Bell } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { backToHereState } from '@/hooks/useInAppBack';
import {
  useNotificationCount,
  useNotificationFeed,
  useResolveNotification,
} from '@/hooks/useNotifications';
import { adminWorkflowHref } from '@/lib/workflowRoutes';
import type { NotificationListItem } from '@/types/notifications';
import { NotificationItem } from './NotificationItem';

interface NotificationCenterPanelProps {
  /** Called after navigating away (e.g. to close the popover). */
  onNavigated?: () => void;
}

export function NotificationCenterPanel({
  onNavigated,
}: NotificationCenterPanelProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, isError } = useNotificationFeed();
  const { data: counts } = useNotificationCount();
  const resolve = useResolveNotification();

  const items = data?.items ?? [];
  // True open count (matches the sidebar badge); items is capped at the page size.
  const openCount = counts?.open ?? items.length;

  const handleSelect = (item: NotificationListItem) => {
    const href = adminWorkflowHref(item.subjectType, item.subjectId);
    if (!href) return;
    // The panel overlays whatever page is open — carry it as back state so
    // the record's back arrow pops to where the user actually was, matching
    // list-originated visits.
    navigate(href, { state: backToHereState(location, 'Back') });
    onNavigated?.();
  };

  const handleViewAll = () => {
    navigate('/notifications');
    onNavigated?.();
  };

  return (
    <div className="flex max-h-[28rem] flex-col">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="text-body-small-strong-b">Notifications</h2>
        {openCount > 0 && (
          <span className="text-caption-long-secondary">{openCount} open</span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <p className="text-caption-long-secondary px-4 py-6 text-center">
            Loading…
          </p>
        ) : isError ? (
          <p className="text-caption-long-secondary px-4 py-6 text-center">
            Couldn’t load notifications.
          </p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
            <Bell className="h-6 w-6 text-long-secondary" aria-hidden />
            <p className="text-caption-long-secondary">You’re all caught up.</p>
          </div>
        ) : (
          <ul className="divide-y">
            {items.map(item => (
              <li key={item.id}>
                <NotificationItem
                  item={item}
                  onSelect={handleSelect}
                  onResolve={resolve.mutate}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t">
        <button
          type="button"
          onClick={handleViewAll}
          className="text-caption-label text-link hover:bg-link-background w-full px-4 py-3 text-center font-medium"
        >
          View Notifications
        </button>
      </div>
    </div>
  );
}
